import Image from 'next/image'
import React, { MouseEventHandler } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AttributesProvider } from '@hozana/dom/components/AttributesProvider'
import { useDispatch } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { concatUrl } from '@hozana/router/concatUrl'
import { useAdminRoutes } from '@hozana/router/useAdminRoutes'
import { isKindOfApp } from '@hozana/screen/functions/isKindOfApp'
import { QA } from '@hozana/tests/constants'
import { GTM_AUTH_SOURCE, TRACKING_SOURCE, UTM } from '@hozana/tracking/constants'

import { Button } from 'elements/button/Button'
import { RoundButton } from 'elements/button/RoundButton'
import { AbsoluteDiv } from 'elements/layout/AbsoluteDiv'
import { Div } from 'elements/layout/Div'
import { FixedDiv, TFixedDivProps } from 'elements/layout/FixedDiv'
import { ScrollDiv } from 'elements/layout/ScrollDiv'
import { Space } from 'elements/layout/Space'
import { Ul } from 'elements/layout/Ul'
import { P } from 'elements/text/P'
import { Text } from 'elements/text/Text'

import { ICON } from 'config/icons'
import { openPopin } from 'general/actions'
import { MainLink } from 'general/components/MainLink'
import { MainLinkDonate } from 'general/components/MainLinkDonate'
import { SecondaryLink } from 'general/components/SecondaryLink'
import { DONATION_SOURCE, JOBS_PAGE_LINK, NAVIGATION_CLASS, NAVIGATION_ID, QUERY_ACTION } from 'general/constants'
import { POPINS } from 'general/managers/popins/constants'
import { GuidesLinkGrid } from 'general/structure/Navigation/GuidesLinkGrid'
import { PAGE, newRoutes } from 'routes/constants'

import { GUIDES, GUIDE_TYPE } from 'modules/guide/constants'
import { useUser } from 'modules/user/hooks/useUser'

import { BlockLink } from './BlockLink'
import { SwitchLangButton } from './SwitchLangButton'

type TStyledFixedDivProps = {
  isOpen?: boolean
} & TFixedDivProps

const StyledFixedDiv = styled(FixedDiv)<TStyledFixedDivProps>`
  overflow: auto;
  transform: translate3d(${(props) => (props.isOpen ? '0' : '-100%')}, 0, 0);
  transition: transform 0.3s;
  box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.8);
`

type TNavigationProps = {
  toggleNavigation: VoidFunction
} & Pick<TStyledFixedDivProps, 'isOpen'>

export const Navigation: TFC<TNavigationProps> = ({ isOpen, toggleNavigation }) => {
  const {
    i18n: { language },
    t,
  } = useTranslation()
  const dispatch = useDispatch()
  const me = useUser()
  const { pathname } = useRouter()
  const adminRoutes = useAdminRoutes()

  const redirectUrlConnectDetail = pathname === PAGE.HOME ? { reloadUrl: newRoutes.prayerSpace(), reload: true } : {}
  const isOnCommunityPage = ([PAGE.COMMUNITY_VIEW, PAGE.COMMUNITY_VIEW_LANDING] as PAGE[]).includes(pathname)

  const createOnClickConnect =
    (button: 'login' | 'signup'): MouseEventHandler<HTMLButtonElement> =>
    () => {
      toggleNavigation()
      dispatch(
        openPopin(POPINS.ConnectPopin, {
          authSource: button === 'login' ? GTM_AUTH_SOURCE.NAVIGATION_LOGIN : GTM_AUTH_SOURCE.NAVIGATION_SIGNUP,
          connectDetails: { displayWizardOnSignup: true, ...redirectUrlConnectDetail },
        }),
      )
    }

  return (
    <AttributesProvider attributes={isOpen ? null : { tabIndex: -1 }}>
      <StyledFixedDiv
        isOpen={isOpen}
        top
        bottom
        left
        w={{ xs: 1, sm: '375px' }}
        bg="white"
        maxH="100%"
        maxW="100%"
        noPrint
        zIndex="menu"
      >
        <Div
          as="nav"
          data-testid={QA.COMMON.NAVIGATION.NAVIGATION_PANEL}
          h="100%"
          aria-label={t('common:app.navigation.secondary')}
          id={NAVIGATION_ID}
          role="navigation"
        >
          <ScrollDiv hideScrollbar p="4em 1em 50px 1em" className={NAVIGATION_CLASS}>
            {me.isLogged && me.id ? (
              <>
                {/* WIZARD */}
                <BlockLink
                  to={newRoutes.preferences({ source: TRACKING_SOURCE.NAVIGATION })}
                  reload
                  icon={ICON.STARS}
                  bg="primaryBgMedium"
                  color="primaryTextContrastDefault"
                >
                  trans:common:user.wizard.my-wizard
                </BlockLink>
                <Space />

                <Ul>
                  <MainLink
                    data-testid={QA.COMMON.NAVIGATION.PROFILE_LINK}
                    icon={ICON.PROFIL}
                    to={newRoutes.profile()}
                    reload
                    onClick={toggleNavigation}
                  >
                    trans:common:user.profile.my-profile
                  </MainLink>
                  {me.communitiesCount > 0 && (
                    <MainLink icon={ICON.USER_EDIT} to={adminRoutes.animatorSpace()} reload>
                      trans:common:community.toolbar.title
                    </MainLink>
                  )}
                  <MainLink
                    data-testid={QA.COMMON.NAVIGATION.SETTINGS_LINK}
                    icon={ICON.SETTINGS}
                    to={newRoutes.settings()}
                    onClick={toggleNavigation}
                    reload
                  >
                    trans:common:user.parameters
                  </MainLink>
                </Ul>
              </>
            ) : (
              <>
                {/* SIGNIN / SIGNUP */}
                <Div align="middleCenter">
                  <Button
                    shadow
                    size="big"
                    colors="yellow"
                    data-testid={QA.COMMON.NAVIGATION.SIGNUP_LINK}
                    onClick={createOnClickConnect('signup')}
                  >
                    trans:common:auth.signup.signup
                  </Button>
                  <Button
                    shadow
                    size="big"
                    colors="whiteYellow"
                    color="primaryTextContrastDefault"
                    data-testid={QA.COMMON.NAVIGATION.LOGIN_LINK}
                    onClick={createOnClickConnect('login')}
                  >
                    trans:common:auth.login.login
                  </Button>
                </Div>
                <Space />

                {/* COMMUNITIES, INTENTIONS, LIVE */}
                <Ul>
                  <MainLink
                    data-testid={QA.COMMON.NAVIGATION.COMMUNITIES_LINK}
                    icon={ICON.MAGNIFIER}
                    to={newRoutes.communities()}
                    reload
                    onClick={toggleNavigation}
                  >
                    trans:common:community.communities.discover
                  </MainLink>
                  <MainLink
                    icon={ICON.HOZANA_INTENTION}
                    to={newRoutes.intentions({ source: TRACKING_SOURCE.NAVIGATION })}
                    reload
                    onClick={toggleNavigation}
                  >
                    trans:common:navigation.intentions
                  </MainLink>
                </Ul>
              </>
            )}
            {/* APPLI */}
            <Div m="1em 0">
              {!isKindOfApp() ? (
                <BlockLink
                  data-testid={QA.COMMON.NAVIGATION.APP_STORE_LINK}
                  to={adminRoutes.app({ utmMedium: UTM.MEDIUM.APP_MENU_LINK })}
                  icon={ICON.MOBILE}
                  bg="primaryBgMedium"
                  color="primaryTextContrastDefault"
                  onClick={toggleNavigation}
                  reload
                >
                  trans:common:home.welcome.app-section.title
                </BlockLink>
              ) : (
                <BlockLink
                  to={concatUrl(newRoutes.prayerSpace(), { action: QUERY_ACTION.INVITE })}
                  icon={ICON.SEND}
                  bg="darkBlue"
                  onClick={toggleNavigation}
                >
                  trans:common:share.invite-friends.long
                </BlockLink>
              )}
            </Div>
            {/* FAQ, JOBS, DONATE */}
            <Ul>
              {language === 'fr' && (
                <>
                  <MainLink
                    to={newRoutes.guide(GUIDES.fr[GUIDE_TYPE.LIVE])}
                    data-testid={QA.COMMON.NAVIGATION.LIVE_LINK}
                    icon={ICON.LIVE}
                    onClick={toggleNavigation}
                  >
                    trans:common:navigation.live-page-link
                  </MainLink>
                  <MainLink icon={ICON.EVENT} to={newRoutes.page('evenements')} onClick={toggleNavigation}>
                    trans:common:app.navigation.events-link
                  </MainLink>
                  <MainLink icon={ICON.JOBS} to={JOBS_PAGE_LINK} onClick={toggleNavigation}>
                    trans:common:app.navigation.jobs-link
                  </MainLink>
                </>
              )}
              <MainLink
                icon={ICON.MESSAGE_QUESTION}
                data-testid={QA.COMMON.NAVIGATION.MISC_LINKS('faq')}
                to={newRoutes.faq()}
                reload
                onClick={toggleNavigation}
              >
                trans:common:app.navigation.footer.faq.link
              </MainLink>
              <MainLinkDonate source={DONATION_SOURCE.MENU} data-testid={QA.COMMON.NAVIGATION.DONATE_LINK} />
            </Ul>
            {/* GUIDES */}
            <Space />
            <P align="center" color="black50" m="0 0 10px 0">
              trans:common:navigation.guides.title
            </P>
            <GuidesLinkGrid onClick={toggleNavigation} />
            <Space />
            {!me.isLogged && !isOnCommunityPage && (
              <>
                <SwitchLangButton />
                <Space />
              </>
            )}
            {/* SECONDARY LINKS */}
            <Ul>
              <SecondaryLink data-testid={QA.COMMON.NAVIGATION.MISC_LINKS('contact')} to={newRoutes.contact()} reload>
                trans:common:words.contact-us
              </SecondaryLink>
              <SecondaryLink
                data-testid={QA.COMMON.NAVIGATION.MISC_LINKS('introduction')}
                to={newRoutes.page('introduction')}
                reload
              >
                trans:common:home.faq.title.head
              </SecondaryLink>
              <SecondaryLink data-testid={QA.COMMON.NAVIGATION.MISC_LINKS('about')} to={newRoutes.page('about')}>
                trans:common:app.navigation.footer.who.we.are.link
              </SecondaryLink>
              <SecondaryLink
                data-testid={QA.COMMON.NAVIGATION.MISC_LINKS(PAGE.COMMUNITY_LIST_SEO)}
                to={{ pathname: PAGE.COMMUNITY_LIST_SEO }}
              >
                trans:common:community.all
              </SecondaryLink>

              <SecondaryLink to={newRoutes.guidesList()}>trans:common:app.navigation.footer.guide.all</SecondaryLink>

              <SecondaryLink data-testid={QA.COMMON.NAVIGATION.MISC_LINKS('cgu')} to={newRoutes.page('cgu')} reload>
                trans:common:app.navigation.footer.general.terms.use.link
              </SecondaryLink>
            </Ul>
            <Div align="center" p="1.5rem">
              <Image src="/img/hozana_logo.png" alt="Hozana" width={162} height={33} />
              <Space h="1.5em" />
              <P color="black50">
                Hozana {CONF_KEYS.APP_VERSION} -{' '}
                <Text>
                  {/dev|preprod|staging/.test(CONF_KEYS.ENV) ? CONF_KEYS.GIT_BRANCH : 'trans:common:word.news'}
                </Text>
              </P>
            </Div>
            <AbsoluteDiv right="7px" top="7px">
              <RoundButton
                icon={ICON.CLOSE}
                onClick={toggleNavigation}
                color="black30"
                hoverColor="black50"
                size="2em"
                label="trans:common:word.close"
                ariaLabel="trans:common:app.navigation.close"
                tabIndex={isOpen ? null : -1}
                data-testid={QA.COMMON.NAVIGATION.CLOSE_BUTTON}
              />
            </AbsoluteDiv>
          </ScrollDiv>
        </Div>
      </StyledFixedDiv>
    </AttributesProvider>
  )
}
